import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Card, Button, Toast, Tag, Divider } from 'antd-mobile';
import { LeftOutline, ClockCircleOutline, MessageOutline, UserOutline } from 'antd-mobile-icons';
import { useNavigate, useParams } from 'react-router-dom';
import { getSessionDetail, getSessionDetailById, getSessionMessages, type MessageInfo } from '../../lib/api/conversations';

const Container = styled.div`
  min-height: 100vh;
  background: #f5f5f5;
  padding-bottom: 20px;
`;

const Header = styled.div`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 20px;
  position: relative;
`;

const BackButton = styled.div`
  position: absolute;
  top: 20px;
  left: 20px;
  z-index: 10;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
`;

const HeaderContent = styled.div`
  padding-top: 40px;
`;

const HeaderTitle = styled.h1`
  font-size: 20px;
  font-weight: bold;
  margin: 0 0 8px 0;
  color: white;
`;

const HeaderInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.9);
`;

const InfoItem = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const ContentSection = styled.div`
  padding: 16px;
`;

const SessionInfoCard = styled(Card)`
  margin-bottom: 16px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
  
  &:last-child {
    border-bottom: none;
  }
`;

const InfoLabel = styled.span`
  color: #666;
  font-size: 14px;
`;

const InfoValue = styled.span`
  color: #333;
  font-size: 14px;
  font-weight: 500;
`;

const MessagesSection = styled.div`
  margin-top: 16px;
`;

const SectionTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 12px 0;
  color: #333;
`;

const MessageCard = styled.div<{ isUser: boolean }>`
  display: flex;
  margin-bottom: 16px;
  justify-content: ${props => props.isUser ? 'flex-end' : 'flex-start'};
`;

const MessageBubble = styled.div<{ isUser: boolean }>`
  max-width: 80%;
  padding: 12px 16px;
  border-radius: 16px;
  background: ${props => props.isUser ? '#1890ff' : '#ffffff'};
  color: ${props => props.isUser ? '#ffffff' : '#333333'};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  position: relative;
`;

const MessageHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  font-size: 12px;
  opacity: 0.8;
`;

const MessageContent = styled.div`
  line-height: 1.5;
  font-size: 14px;
`;

const MessageTime = styled.div`
  font-size: 10px;
  opacity: 0.6;
  margin-top: 4px;
  text-align: right;
`;

const RoundDivider = styled.div`
  display: flex;
  align-items: center;
  margin: 24px 0 16px 0;
  color: #999;
  font-size: 12px;
  
  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: #e8e8e8;
  }
  
  span {
    padding: 0 16px;
    background: #f5f5f5;
    border-radius: 12px;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #999;
`;

const EmptyIcon = styled.div`
  font-size: 48px;
  margin-bottom: 16px;
  opacity: 0.5;
`;

interface SessionDetailInfo {
  id: string;
  session_token: string;
  scenario_name: string;
  status: number;
  status_name: string;
  round_number: number;
  created_at: string;
  updated_at: string;
}

const SessionDetail: React.FC = () => {
  const navigate = useNavigate();
  const { sessionId } = useParams<{ sessionId: string }>();
  const [loading, setLoading] = useState(false);
  const [sessionInfo, setSessionInfo] = useState<SessionDetailInfo | null>(null);
  const [messages, setMessages] = useState<MessageInfo[]>([]);

  const fetchSessionDetail = async () => {
    if (!sessionId) return;
    
    setLoading(true);
    try {
      // 判断sessionId是否为UUID格式（session ID）还是token格式
        const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(sessionId);
        
        let sessionResponse;
        if (isUUID) {
          // 使用session ID获取详情
          sessionResponse = await getSessionDetailById(sessionId);
        } else {
          // 使用session token获取详情
          sessionResponse = await getSessionDetail(sessionId);
        }
        
        const messagesResponse = await getSessionMessages(sessionId, { page: 1, pageSize: 100 });
      
      if (sessionResponse.success && sessionResponse.data) {
        setSessionInfo({
          id: sessionResponse.data.session_id,
          session_token: sessionResponse.data.session_token,
          scenario_name: sessionResponse.data.scenario_name || '未知场景',
          status: sessionResponse.data.status,
          status_name: sessionResponse.data.status_name || '未知状态',
          round_number: sessionResponse.data.round_number || 0,
          created_at: sessionResponse.data.created_at || new Date().toISOString(),
          updated_at: sessionResponse.data.updated_at || new Date().toISOString()
        });
      }
      
      if (messagesResponse.success && messagesResponse.data) {
        setMessages(messagesResponse.data.list || []);
      }
    } catch (error) {
      console.error('获取会话详情失败:', error);
      Toast.show({
        content: '获取会话详情失败',
        icon: 'fail'
      });
    } finally {
      setLoading(false);
    }
  };
  
  // 如果API调用失败，使用mock数据作为fallback
  const useMockData = () => {
    const mockSessionInfo: SessionDetailInfo = {
      id: '1',
      session_token: sessionId || '',
      scenario_name: '银行理财产品销售',
      status: 2,
      status_name: '已完成',
      round_number: 4,
      created_at: '2024-01-15T10:00:00Z',
      updated_at: '2024-01-15T10:15:00Z'
    };
    
    const mockMessages: MessageInfo[] = [
      {
        id: '1',
        round_number: 1,
        speaker: 'ai' as any,
        message_type: 'text' as any,
        content: '您好！欢迎来到我们银行，我是您的专属理财顾问小王。今天想了解一下您的理财需求，请问您目前有什么投资计划吗？',
        timestamp: '2024-01-15T10:00:30Z',
        step: { id: '1', name: '开场白' },
        audio_file_path: undefined,
        audio_duration: undefined,
        tts_voice_id: undefined,
        asr_confidence: undefined
      },
      {
        id: '2',
        round_number: 1,
        speaker: 'user' as any,
        message_type: 'text' as any,
        content: '你好，我想了解一下你们银行的理财产品，我有10万元想要投资，希望收益稳定一些。',
        timestamp: '2024-01-15T10:01:00Z',
        step: { id: '1', name: '开场白' },
        audio_file_path: undefined,
        audio_duration: undefined,
        tts_voice_id: undefined,
        asr_confidence: undefined
      }
    ];
    
    setSessionInfo(mockSessionInfo);
    setMessages(mockMessages);
  };

  useEffect(() => {
    fetchSessionDetail();
  }, [sessionId]);

  const handleBack = () => {
    navigate(-1);
  };

  const getStatusText = (status: number) => {
    switch (status) {
      case 1: return '进行中';
      case 2: return '已完成';
      case 3: return '已中断';
      case 4: return '错误';
      default: return '未知';
    }
  };

  const getStatusColor = (status: number) => {
    switch (status) {
      case 1: return '#52c41a';
      case 2: return '#1890ff';
      case 3: return '#faad14';
      case 4: return '#ff4d4f';
      default: return '#d9d9d9';
    }
  };

  const formatTime = (timeString: string) => {
    return new Date(timeString).toLocaleString('zh-CN');
  };

  const formatDuration = (seconds?: number) => {
    if (!seconds) return '-';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const formatMessageTime = (timeString: string) => {
    return new Date(timeString).toLocaleTimeString('zh-CN', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // 按轮次分组消息
  const groupedMessages = messages.reduce((groups, message) => {
    const round = message.round_number;
    if (!groups[round]) {
      groups[round] = [];
    }
    groups[round].push(message);
    return groups;
  }, {} as Record<number, MessageInfo[]>);

  if (loading) {
    return (
      <Container>
        <Header>
          <BackButton onClick={handleBack}>
            <LeftOutline fontSize={18} />
          </BackButton>
          <HeaderContent>
            <HeaderTitle>对话详情</HeaderTitle>
          </HeaderContent>
        </Header>
        <div style={{ padding: '20px', textAlign: 'center' }}>
          加载中...
        </div>
      </Container>
    );
  }

  if (!sessionInfo) {
    return (
      <Container>
        <Header>
          <BackButton onClick={handleBack}>
            <LeftOutline fontSize={18} />
          </BackButton>
          <HeaderContent>
            <HeaderTitle>对话详情</HeaderTitle>
          </HeaderContent>
        </Header>
        <div style={{ padding: '20px', textAlign: 'center' }}>
          对话不存在
        </div>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <BackButton onClick={handleBack}>
          <LeftOutline fontSize={18} />
        </BackButton>
        <HeaderContent>
          <HeaderTitle>对话详情</HeaderTitle>
          <HeaderInfo>
            <InfoItem>
              <Tag color={getStatusColor(sessionInfo.status)} style={{ border: 'none' }}>
                {getStatusText(sessionInfo.status)}
              </Tag>
            </InfoItem>
            <InfoItem>
              <MessageOutline fontSize={14} />
              <span>{sessionInfo.round_number} 轮</span>
            </InfoItem>
            <InfoItem>
              <ClockCircleOutline fontSize={14} />
              <span>{messages.length} 条消息</span>
            </InfoItem>
          </HeaderInfo>
        </HeaderContent>
      </Header>

      <ContentSection>
        {/* 会话信息 */}
        <SessionInfoCard>
          <div style={{ padding: '16px' }}>
            <SectionTitle>会话信息</SectionTitle>
            {sessionInfo.scenario_name && (
              <InfoRow>
                <InfoLabel>场景名称</InfoLabel>
                <InfoValue>{sessionInfo.scenario_name}</InfoValue>
              </InfoRow>
            )}
            <InfoRow>
              <InfoLabel>会话ID</InfoLabel>
              <InfoValue>{sessionInfo.session_token}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>开始时间</InfoLabel>
              <InfoValue>{formatTime(sessionInfo.created_at)}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>更新时间</InfoLabel>
              <InfoValue>{formatTime(sessionInfo.updated_at)}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>对话轮次</InfoLabel>
              <InfoValue>{sessionInfo.round_number} 轮</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>消息总数</InfoLabel>
              <InfoValue>{messages.length} 条</InfoValue>
            </InfoRow>
          </div>
        </SessionInfoCard>

        {/* 对话消息 */}
        <MessagesSection>
          <SectionTitle>对话记录</SectionTitle>
          {Object.keys(groupedMessages).length === 0 ? (
            <EmptyState>
              <EmptyIcon>💬</EmptyIcon>
              <div>暂无对话消息</div>
            </EmptyState>
          ) : (
            Object.keys(groupedMessages)
              .sort((a, b) => Number(a) - Number(b))
              .map((round) => (
                <div key={round}>
                  <RoundDivider>
                    <span>第 {round} 轮</span>
                  </RoundDivider>
                  {groupedMessages[Number(round)].map((message) => (
                    <MessageCard key={message.id} isUser={message.speaker === 'user'}>
                      <MessageBubble isUser={message.speaker === 'user'}>
                        <MessageHeader>
                          {message.speaker === 'user' ? (
                            <UserOutline fontSize={12} />
                          ) : (
                            <UserOutline fontSize={12} />
                          )}
                          <span>{message.speaker === 'user' ? '我' : 'AI助手'}</span>
                        </MessageHeader>
                        <MessageContent>{message.content}</MessageContent>
                        <MessageTime>{formatMessageTime(message.timestamp)}</MessageTime>
                      </MessageBubble>
                    </MessageCard>
                  ))}
                </div>
              ))
          )}
        </MessagesSection>
      </ContentSection>
    </Container>
  );
};

export default SessionDetail;